import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	IconButton,
	Divider
} from '@mui/material';
import axios from 'axios';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as locales from 'date-fns/locale';
import { isWeekend, addBusinessDays } from 'date-fns';
import i18next, { getBrowserLanguage } from '../lang';
import {rootStore} from "../stores/RootStore";
import {useNavigate} from 'react-router-dom';

export interface OrderData {
	line: number;
	userId: string;
	productType: string;
	productId: number;
	classroomName: string;
	aliasName: string;
	productTitle: string;
	productVolume: string;
	subjectCode: string;
	quantity: number;
	deliveryDate: string;
	timeSlot: string;
}

export interface InvalidOrderData {
	order: OrderData;
	invalidClassroomName: boolean;
	invalidProductTitle: boolean;
	invalidProductVolume: boolean;
	invalidSubjectCode: boolean;
	invalidQuantity: boolean;
	invalidDeliveryDate: boolean;
	reason: string;
}

export interface DuplicateClassroomOrderData {
	order: OrderData;
	classrooms: DuplicateClassroomData[];
}

interface DuplicateClassroomData {
	userId: string;
	address: string;
}

export interface DuplicateProductOrderData {
	order: OrderData;
	productType: string;
	products: ProductData[];
	productSets: ProductSetData[];
}

interface ProductData {
	id: number;
	curriculumName: string;
	title: string;
	volume: string;
	price: number;
	code: string;
}

interface ProductSetData {
	id: number;
	name: string;
	description: string;
	price: number;
}

interface OrderUploadDialogProps {
	open: boolean;
	onClose: () => void;
	totalOrderCount: number;
	normalOrders: OrderData[];
	invalidOrders: InvalidOrderData[];
	duplicateClassroomOrders: DuplicateClassroomOrderData[];
	duplicateProductOrders: DuplicateProductOrderData[];
}

const OrderUploadDialog: React.FC<OrderUploadDialogProps> = ({
	open,
	onClose,
	totalOrderCount,
	normalOrders: initialNormalData,
	invalidOrders: initialInvalidData,
	duplicateClassroomOrders: initialDuplicateClassroomData,
	duplicateProductOrders: initialDuplicateProductData
}) => {
	const navigate = useNavigate();
	const { viewStatusStore } = rootStore;
	const [normalOrders, setNormalOrders] = useState<OrderData[]>([]);
	const [invalidOrders, setInvalidOrders] = useState<InvalidOrderData[]>([]);
	const [duplicateClassroomOrders, setDuplicateClassroomOrders] = useState<DuplicateClassroomOrderData[]>([]);
	const [duplicateProductOrders, setDuplicateProductOrders] = useState<DuplicateProductOrderData[]>([]);
	const [requestOrders, setRequestOrders] = useState<OrderData[]>([]);
	const [deleteOrders, setDeleteOrders] = useState<number[]>([]);

	useEffect(() => {
		setNormalOrders(initialNormalData);
		setInvalidOrders(initialInvalidData);
		setDuplicateClassroomOrders(initialDuplicateClassroomData);
		setDuplicateProductOrders(initialDuplicateProductData);
	}, [initialNormalData, initialInvalidData, initialDuplicateClassroomData, initialDuplicateProductData]);

	const handleInvalidDataChange = (order: OrderData, field: keyof OrderData, value: string | number | Date | null) => {
		const existingOrderIndex = requestOrders.findIndex(reqOrder => reqOrder.line === order.line);
		
		if (existingOrderIndex === -1) {
			const newOrder = { ...order, [field]: value };
			setRequestOrders(prev => [...prev, newOrder]);
		} else {
			setRequestOrders(prev =>
				prev.map(reqOrder =>
					reqOrder.line === order.line
						? { ...reqOrder, [field]: value }
						: reqOrder
				)
			);
		}
	};

	const getDisabledDates = () => {
		const today = new Date();
		const disabledDate = addBusinessDays(today, 5);
		return (date: Date) => {
			return date <= disabledDate || isWeekend(date);
		};
	};

	const handleDuplicateClassChange = (order: OrderData, value: string) => {
		const existingOrderIndex = requestOrders.findIndex(reqOrder => reqOrder.line === order.line);

		if (existingOrderIndex === -1) {
			const newOrder = { ...order, userId: value };
			setRequestOrders(prev => [...prev, newOrder]);
		} else {
			setRequestOrders(prev =>
				prev.map(reqOrder =>
					reqOrder.line === order.line
						? { ...reqOrder, userId: value }
						: reqOrder
				)
			);
		}
	};

	const handleDuplicateProductChange = (order: OrderData, value: string) => {
		const existingOrderIndex = requestOrders.findIndex(reqOrder => reqOrder.line === order.line);

		const lastHyphenIndex = value.lastIndexOf('-');
		const productType = value.slice(0, lastHyphenIndex);
		const productId = parseInt(value.slice(lastHyphenIndex + 1), 10);

		if (existingOrderIndex === -1) {
			const newOrder = { ...order, productType, productId };
			setRequestOrders(prev => [...prev, newOrder]);
		} else {
			setRequestOrders(prev =>
				prev.map(reqOrder =>
					reqOrder.line === order.line
						? { ...reqOrder, productType, productId }
						: reqOrder
				)
			);
		}
	};

	const handleDeleteOrder = async (line: number, field: string) => {
		setDeleteOrders(prev => [...prev, line]);

		let newInvalidOrders = invalidOrders;
		let newDuplicateClassroomOrders = duplicateClassroomOrders;
		let newDuplicateProductOrders = duplicateProductOrders;

		if (field === 'invalid') {
			newInvalidOrders = invalidOrders.filter(order => order.order.line !== line);
			setInvalidOrders(newInvalidOrders);
		} else if (field === 'class') {
			newDuplicateClassroomOrders = duplicateClassroomOrders.filter(order => order.order.line !== line);
			setDuplicateClassroomOrders(newDuplicateClassroomOrders);
		} else if (field === 'product') {
			newDuplicateProductOrders = duplicateProductOrders.filter(order => order.order.line !== line);
			setDuplicateProductOrders(newDuplicateProductOrders);
		}

		setRequestOrders(prev => prev.filter(order => order.line !== line));

		if (
			newInvalidOrders.length === 0 &&
			newDuplicateClassroomOrders.length === 0 &&
			newDuplicateProductOrders.length === 0
		) {
			onClose();
			if (normalOrders.length > 0) {
				await requestConvertData(normalOrders);
			}
		}
	};

	const isConfirmButtonEnabled = () => {
		if (totalOrderCount !== (normalOrders.length + deleteOrders.length + requestOrders.length)) {
			return false;
		} else {
			return requestOrders.every(order => {
				if (!order.classroomName || order.classroomName.trim() === '') {
					return false;
				}

				if (!order.productTitle || order.productTitle.trim() === '') {
					return false;
				}

				if (!order.quantity || order.quantity <= 0) {
					return false;
				}

				return true;
			});
		}
	};

	const handleConfirm = async () => {
		setInvalidOrders([]);
		setDuplicateClassroomOrders([]);
		setDuplicateProductOrders([]);
		try {
			viewStatusStore.setLoadingStatus({ open: true });
			const response = await axios.post('/api/v1/orders/excel', requestOrders);

			if (response.data && (response.data.invalidOrders?.length > 0 || response.data.duplicateClassroomOrders?.length > 0 || response.data.duplicateProductOrders?.length > 0)) {
				if (response.data.normalOrders && response.data.normalOrders.length > 0) {
					setNormalOrders(prev => {
						const merged = [...prev, ...response.data.normalOrders];
						return merged.sort((a, b) => a.line - b.line);
					});
				}
				setRequestOrders([]);
				setInvalidOrders(response.data.invalidOrders);
				setDuplicateClassroomOrders(response.data.duplicateClassroomOrders);
				setDuplicateProductOrders(response.data.duplicateProductOrders);
			} else {
				onClose();
				if (response.data.normalOrders && response.data.normalOrders.length > 0) {
					const merged = [...normalOrders, ...response.data.normalOrders].sort((a, b) => a.line - b.line);
					setNormalOrders(merged);
					await requestConvertData(merged);
				}
			}
		} catch (error) {
			console.error('주문 데이터 업로드 오류:', error);
		} finally {
			viewStatusStore.setLoadingStatus({ open: false });
		}
	};

	const requestConvertData = async (orders) => {
		try {
			viewStatusStore.setLoadingStatus({ open: true });
			const response = await axios.post('/api/v1/orders/convert', orders);

			navigate('/purchase/order-final-confirm', {
				state: {
					orderData: response.data,
					excelData: orders
				}
			});
		} catch (error) {
			console.error('주문 데이터 변환 오류:', error);
		} finally {
			viewStatusStore.setLoadingStatus({ open: false });
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Typography variant="h6" component="div">
					{i18next.t('purchase.order_data_verification')}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
					{invalidOrders.length > 0 &&
						<>
							<Box>
								<Typography variant="h6" gutterBottom color="error">
									{i18next.t('purchase.invalid_data')} ({invalidOrders.length})
								</Typography>
								<Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 10 }}>
														{i18next.t('common.row')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
														{i18next.t('order.classroom')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
														{i18next.t('order.textbook_name')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
														{i18next.t('product.volume')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'center' }}>
														{i18next.t('product.subject_code')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 40, textAlign: 'center' }}>
														{i18next.t('order.quantity')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 120, textAlign: 'center' }}>
														{i18next.t('order.delivery_date')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'center' }}>
														{i18next.t('order.time_slot')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
														{i18next.t('common.delete')}
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{invalidOrders.map((order, index) => (
													<TableRow key={index}>
														<TableCell>
															{order.order.line}
														</TableCell>
														<TableCell>
															{order.invalidClassroomName ?
																<TextField
																	size="small"
																	fullWidth
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line) !== undefined ? requestOrders.find(reqOrder => reqOrder.line === order.order.line).classroomName : order.order.classroomName}
																	onChange={(e) => handleInvalidDataChange(order.order, 'classroomName', e.target.value)}
																/>
																:
																order.order.classroomName
															}
														</TableCell>
														<TableCell>
															{order.invalidProductTitle || order.invalidProductVolume || order.invalidSubjectCode ?
																<TextField
																	size="small"
																	fullWidth
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line) !== undefined ? requestOrders.find(reqOrder => reqOrder.line === order.order.line).productTitle : order.order.productTitle}
																	onChange={(e) => handleInvalidDataChange(order.order, 'productTitle', e.target.value)}
																/>
																:
																order.order.productTitle
															}
														</TableCell>
														<TableCell>
															{order.invalidProductTitle || order.invalidProductVolume || order.invalidSubjectCode ?
																<TextField
																	size="small"
																	fullWidth
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line) !== undefined ? requestOrders.find(reqOrder => reqOrder.line === order.order.line).productVolume : order.order.productVolume}
																	onChange={(e) => handleInvalidDataChange(order.order, 'productVolume', e.target.value)}
																/>
																:
																order.order.productVolume ? order.order.productVolume : '-'
															}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.invalidProductTitle || order.invalidProductVolume || order.invalidSubjectCode ?
																<TextField
																	size="small"
																	fullWidth
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line) !== undefined ? requestOrders.find(reqOrder => reqOrder.line === order.order.line).subjectCode : order.order.subjectCode}
																	onChange={(e) => handleInvalidDataChange(order.order, 'subjectCode', e.target.value)}
																/>
																:
																order.order.subjectCode ? order.order.subjectCode : '-'
															}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.invalidQuantity ?
																<TextField
																	size="small"
																	fullWidth
																	type="number"
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line)?.quantity || order.order.quantity}
																	onChange={(e) => handleInvalidDataChange(order.order, 'quantity', parseInt(e.target.value) || 0)}
																/>
																:
																order.order.quantity
															}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.invalidDeliveryDate ?
																<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
																	<DatePicker
																		value={requestOrders.find(reqOrder => reqOrder.line === order.order.line)?.deliveryDate ?
																			new Date(requestOrders.find(reqOrder => reqOrder.line === order.order.line)!.deliveryDate) :
																			(order.order.deliveryDate ? new Date(order.order.deliveryDate) : null)}
																		onChange={(newValue) => handleInvalidDataChange(order.order, 'deliveryDate', newValue?.toISOString().split('T')[0] || '')}
																		shouldDisableDate={getDisabledDates()}
																		slotProps={{
																			textField: {
																				inputProps: {
																					readOnly: true,
																				},
																			},
																		}}
																		sx={{
																			width: '100%',
																			'& .MuiInputBase-root': {
																				height: '35px',
																				fontSize: '0.8rem'
																			}
																		}}
																	/>
																</LocalizationProvider>
																:
																order.order.deliveryDate ? order.order.deliveryDate : '-'
															}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.order.timeSlot ? order.order.timeSlot : '-'}
														</TableCell>
														<TableCell>
															<IconButton
																size="small"
																onClick={() => handleDeleteOrder(order.order.line, 'invalid')}
																color="error"
															>
																<DeleteIcon />
															</IconButton>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							</Box>
							<Divider />
						</>
					}

					{duplicateClassroomOrders.length > 0 &&
						<>
							<Box>
								<Typography variant="h6" gutterBottom color="warning.main">
									{i18next.t('purchase.duplicate_classroom')} ({duplicateClassroomOrders.length})
								</Typography>
								<Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 10 }}>
														{i18next.t('common.row')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
														{i18next.t('order.classroom')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
														{i18next.t('order.textbook_name')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
														{i18next.t('product.volume')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'center'  }}>
														{i18next.t('product.subject_code')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 40, textAlign: 'center'  }}>
														{i18next.t('order.quantity')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 120, textAlign: 'center' }}>
														{i18next.t('order.delivery_date')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'center' }}>
														{i18next.t('order.time_slot')}
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
														{i18next.t('common.delete')}
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{duplicateClassroomOrders.map((order, index) => (
													<TableRow key={index}>
														<TableCell>
															{order.order.line}
														</TableCell>
														<TableCell>
															<FormControl size="small" fullWidth>
																<InputLabel>{order.order.classroomName}</InputLabel>
																<Select
																	value={requestOrders.find(reqOrder => reqOrder.line === order.order.line)?.classroomName}
																	onChange={(e) => handleDuplicateClassChange(order.order, e.target.value)}
																	label={i18next.t('purchase.select_classroom')}
																>
																	{order.classrooms.map((classroom) => (
																		<MenuItem key={classroom.userId} value={classroom.userId}>
																			{order.order.classroomName} ({classroom.address})
																		</MenuItem>
																	))}
																</Select>
															</FormControl>
														</TableCell>
														<TableCell>
															{order.order.productTitle}
														</TableCell>
														<TableCell>
															{order.order.productVolume ? order.order.productVolume : '-'}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.order.subjectCode ? order.order.subjectCode : '-'}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.order.quantity}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.order.deliveryDate ? order.order.deliveryDate : '-'}
														</TableCell>
														<TableCell sx={{textAlign: 'center'}}>
															{order.order.timeSlot ? order.order.timeSlot : '-'}
														</TableCell>
														<TableCell>
															<IconButton
																size="small"
																onClick={() => handleDeleteOrder(order.order.line, 'class')}
																color="error"
															>
																<DeleteIcon />
															</IconButton>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Paper>
							</Box>
							<Divider />
						</>
					}

					{duplicateProductOrders.length > 0 &&
						<Box>
							<Typography variant="h6" gutterBottom color="warning.main">
								{i18next.t('purchase.duplicate_textbook')} ({duplicateProductOrders.length})
							</Typography>
							<Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 10 }}>
													{i18next.t('common.row')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
													{i18next.t('order.classroom')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
													{i18next.t('order.textbook_name')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
													{i18next.t('product.volume')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'center'  }}>
													{i18next.t('product.subject_code')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 40, textAlign: 'center'  }}>
													{i18next.t('order.quantity')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 120, textAlign: 'center' }}>
													{i18next.t('order.delivery_date')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'center' }}>
													{i18next.t('order.time_slot')}
												</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
													{i18next.t('common.delete')}
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{duplicateProductOrders.map((order, index) => (
												<TableRow key={index}>
													<TableCell>
														{order.order.line}
													</TableCell>
													<TableCell>
														{order.order.classroomName}
													</TableCell>
													<TableCell>
														<FormControl size="small" fullWidth>
															<InputLabel>{order.order.productTitle}</InputLabel>
															<Select
																value={requestOrders.find(reqOrder => reqOrder.line === order.order.line)?.productTitle}
																onChange={(e) => handleDuplicateProductChange(order.order, e.target.value)}
																label={i18next.t('purchase.select_textbook')}
															>
																{order.products.map((product) => (
																	<MenuItem key={`normal-${product.id}`} value={`normal-${product.id}`}>
																		{product.curriculumName} {product.title} {product.volume || ''} ({product.price || '0'}{i18next.t('common.yen')})
																	</MenuItem>
																))}
																{order.productSets.map((productSet) => (
																	<MenuItem key={`set-${productSet.id}`} value={`set-${productSet.id}`}>
																		[Set] {productSet.name} ({productSet.price || '0'}{i18next.t('common.yen')})
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													</TableCell>
													<TableCell>
														{order.order.productVolume ? order.order.productVolume : '-'}
													</TableCell>
													<TableCell sx={{textAlign: 'center'}}>
														{order.order.subjectCode ? order.order.subjectCode : '-'}
													</TableCell>
													<TableCell sx={{textAlign: 'center'}}>
														{order.order.quantity}
													</TableCell>
													<TableCell sx={{textAlign: 'center'}}>
														{order.order.deliveryDate ? order.order.deliveryDate : '-'}
													</TableCell>
													<TableCell sx={{textAlign: 'center'}}>
														{order.order.timeSlot ? order.order.timeSlot : '-'}
													</TableCell>
													<TableCell>
														<IconButton
															size="small"
															onClick={() => handleDeleteOrder(order.order.line, 'product')}
															color="error"
														>
															<DeleteIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Paper>
						</Box>
					}

				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary">
					{i18next.t('common.cancel')}
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={!isConfirmButtonEnabled()}
					color="primary" variant="contained">
					{i18next.t('common.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default OrderUploadDialog;
